import React, { useState, useEffect, useCallback } from "react";
import {
  Users, Phone, Globe, MessageCircle, TrendingUp, Search, Download,
  Plus, X, CheckCircle, AlertCircle, RefreshCw, LogIn, LogOut,
  Eye, EyeOff, UserCheck, Settings, Trash2,
} from "lucide-react";
import { LEAD_CHANNELS, PROPERTIES } from "../constants/data";
import { leadsAPI, authAPI, adminsAPI, saveLeadToAPI } from "../services/api";

const GOLD = "#50C878";
const STATUS_OPTIONS = ["New", "Contacted", "Qualified", "Closed"];
const STATUS_COLORS = {
  New:       { bg: "rgba(96,165,250,0.15)",  color: "#1B4A7A" },
  Contacted: { bg: "rgba(251,191,36,0.15)",  color: "#7FD9A0" },
  Qualified: { bg: "rgba(34,197,94,0.15)",   color: "#4ade80" },
  Closed:    { bg: "rgba(148,163,184,0.1)",  color: "rgba(1,50,32,0.5)" },
};
const CHANNEL_ICONS = {
  [LEAD_CHANNELS.WEB]:      Globe,
  [LEAD_CHANNELS.WHATSAPP]: MessageCircle,
  [LEAD_CHANNELS.PHONE]:    Phone,
  [LEAD_CHANNELS.CHATBOT]:  MessageCircle,
};

// ── LOGIN SCREEN ──────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail]       = useState("admin@groupbuyflats.com");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [error, setError]       = useState("");
  const [loading, setLoading]   = useState(false);

  async function handleLogin() {
    if (!email || !password) { setError("Enter email and password"); return; }
    setLoading(true); setError("");
    try {
      const res = await authAPI.login(email, password);
      localStorage.setItem("gbf_admin_token", res.token);
      onLogin(res.admin);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally { setLoading(false); }
  }

  const inp = {
    width: "100%", padding: "12px 16px",
    background: "rgba(80,200,120,0.10)",
    border: "1.5px solid rgba(80,200,120,0.3)",
    borderRadius: "8px", color: "#013220",
    fontFamily: "'Inter', sans-serif", fontSize: "0.95rem",
    outline: "none", boxSizing: "border-box",
  };

  return (
    <div style={{ background: "#F2F8F3", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}>
      <div style={{ background: "linear-gradient(135deg,#FFFFFF,#F7FBF8)", border: "1px solid rgba(80,200,120,0.3)", borderRadius: "16px", padding: "40px", width: "100%", maxWidth: "420px", boxShadow: "0 25px 60px rgba(0,0,0,0.7)" }}>
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.8rem", fontWeight: 700, background: "linear-gradient(135deg,#50C878,#7FD9A0)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            GroupBuy Flats
          </span>
          <p style={{ color: "rgba(1,50,32,0.62)", fontFamily: "'Inter',sans-serif", fontSize: "0.85rem", marginTop: "6px" }}>Admin Dashboard</p>
        </div>
        {error && (
          <div style={{ background: "rgba(224,90,90,0.15)", border: "1px solid rgba(224,90,90,0.4)", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", color: "#e05a5a", fontSize: "0.88rem", fontFamily: "'Inter',sans-serif", display: "flex", alignItems: "center", gap: "8px" }}>
            <AlertCircle size={15} /> {error}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
          <div>
            <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.8rem", marginBottom: "6px", fontFamily: "'Inter',sans-serif" }}>Email</label>
            <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="admin@groupbuyflats.com" style={inp}
              onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(80,200,120,0.3)"} />
          </div>
          <div>
            <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.8rem", marginBottom: "6px", fontFamily: "'Inter',sans-serif" }}>Password</label>
            <div style={{ position: "relative" }}>
              <input value={password} onChange={e => setPassword(e.target.value)} type={showPw ? "text" : "password"} placeholder="Enter password"
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                style={{ ...inp, paddingRight: "44px" }}
                onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(80,200,120,0.3)"} />
              <button onClick={() => setShowPw(!showPw)} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: "rgba(1,50,32,0.62)", cursor: "pointer", display: "flex" }}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <button onClick={handleLogin} disabled={loading}
            style={{ width: "100%", padding: "13px", background: loading ? "rgba(80,200,120,0.5)" : "linear-gradient(135deg,#50C878,#7FD9A0)", border: "none", borderRadius: "8px", color: "#FFFFFF", fontFamily: "'Inter',sans-serif", fontWeight: 700, fontSize: "0.95rem", cursor: loading ? "not-allowed" : "pointer", marginTop: "4px", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
            <LogIn size={16} /> {loading ? "Logging in..." : "Login to Dashboard"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── DIRECT CALLER FORM ────────────────────────────────────────
function TelecallerForm({ onSave, onClose }) {
  const [form, setForm] = useState({ firstName: "", lastName: "", phone: "", email: "", message: "", propertyId: "" });
  const [errors, setErrors] = useState({});
  const [saved, setSaved]   = useState(false);
  const [saving, setSaving] = useState(false);

  function cleanPhone(phone) {
    let d = phone.replace(/\D/g, "");
    if (d.startsWith("91") && d.length === 12) d = d.substring(2);
    return d.length === 10 ? d : null;
  }

  function validate() {
    const e = {};
    if (!form.firstName.trim()) e.firstName = "Required";
    if (!form.phone.trim()) e.phone = "Phone is required";
    else if (!cleanPhone(form.phone)) e.phone = "Enter a valid 10-digit Indian mobile number";
    return e;
  }

  async function handleSave() {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const cleanedPhone = cleanPhone(form.phone);
      const propertyTitle = form.propertyId ? PROPERTIES.find(p => p.id === form.propertyId)?.title : null;
      const lead = await saveLeadToAPI({
        first_name: form.firstName.trim(),
        last_name: form.lastName.trim() || null,
        phone: cleanedPhone,
        email: form.email.trim() || null,
        message: form.message.trim() || null,
        property_id: null,
        property_title: propertyTitle,
        channel: LEAD_CHANNELS.PHONE,
      });
      setSaved(true);
      setTimeout(() => { onSave(lead); onClose(); }, 1500);
    } catch (err) {
      setErrors({ submit: "Failed to save lead: " + err.message });
    } finally { setSaving(false); }
  }

  const inputS = f => ({
    width: "100%", padding: "10px 12px",
    background: "rgba(80,200,120,0.10)",
    border: `1.5px solid ${errors[f] ? "#e05a5a" : "rgba(80,200,120,0.25)"}`,
    borderRadius: "6px", color: "#013220",
    fontFamily: "'Inter',sans-serif", fontSize: "0.88rem",
    outline: "none", boxSizing: "border-box",
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ background: "#FFFFFF", border: "1px solid rgba(80,200,120,0.3)", borderRadius: "14px", padding: "28px", width: "100%", maxWidth: "480px", position: "relative" }}>
        <button onClick={onClose} style={{ position: "absolute", top: "14px", right: "14px", background: "none", border: "none", color: "rgba(1,50,32,0.55)", cursor: "pointer" }}><X size={18} /></button>
        <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.5rem", color: "#013220", marginBottom: "6px" }}>Direct Caller</h3>
        <p style={{ color: "rgba(1,50,32,0.62)", fontSize: "0.82rem", fontFamily: "'Inter',sans-serif", marginBottom: "20px" }}>Log a lead from a direct phone call</p>
        {saved ? (
          <div style={{ textAlign: "center", padding: "20px" }}>
            <CheckCircle size={40} color={GOLD} />
            <p style={{ color: "#013220", marginTop: "12px", fontFamily: "'Inter',sans-serif" }}>Lead saved successfully!</p>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {errors.submit && (
              <div style={{ background: "rgba(224,90,90,0.15)", border: "1px solid rgba(224,90,90,0.4)", borderRadius: "6px", padding: "10px", color: "#e05a5a", fontSize: "0.85rem" }}>
                <AlertCircle size={13} style={{ marginRight: "6px" }} />{errors.submit}
              </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div>
                <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.75rem", marginBottom: "4px" }}>First Name *</label>
                <input value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} style={inputS("firstName")} placeholder="First name"
                  onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = errors.firstName ? "#e05a5a" : "rgba(80,200,120,0.25)"} />
                {errors.firstName && <p style={{ color: "#e05a5a", fontSize: "0.72rem", marginTop: "3px" }}>{errors.firstName}</p>}
              </div>
              <div>
                <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.75rem", marginBottom: "4px" }}>Last Name</label>
                <input value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} style={inputS("lastName")} placeholder="Optional"
                  onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(80,200,120,0.25)"} />
              </div>
            </div>
            <div>
              <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.75rem", marginBottom: "4px" }}>Phone *</label>
              <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} style={inputS("phone")} placeholder="9876543210"
                onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = errors.phone ? "#e05a5a" : "rgba(80,200,120,0.25)"} />
              {errors.phone && <p style={{ color: "#e05a5a", fontSize: "0.72rem", marginTop: "3px" }}>{errors.phone}</p>}
            </div>
            <div>
              <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.75rem", marginBottom: "4px" }}>Email</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputS("email")} placeholder="Optional"
                onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(80,200,120,0.25)"} />
            </div>
            <div>
              <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.75rem", marginBottom: "4px" }}>Property Interest</label>
              <select value={form.propertyId} onChange={e => setForm({ ...form, propertyId: e.target.value })} style={{ ...inputS("propertyId"), cursor: "pointer" }}>
                <option value="" style={{ background: "#FFFFFF" }}>Not specified</option>
                {PROPERTIES.map(p => <option key={p.id} value={p.id} style={{ background: "#FFFFFF" }}>{p.title}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.75rem", marginBottom: "4px" }}>Notes from Call</label>
              <textarea value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} rows={3}
                style={{ ...inputS("message"), resize: "vertical", lineHeight: 1.5 }} placeholder="Requirements, queries discussed..."
                onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(80,200,120,0.25)"} />
            </div>
            <button onClick={handleSave} disabled={saving}
              style={{ width: "100%", padding: "12px", background: saving ? "rgba(80,200,120,0.5)" : "linear-gradient(135deg,#50C878,#7FD9A0)", border: "none", borderRadius: "6px", color: "#FFFFFF", fontWeight: 700, fontFamily: "'Inter',sans-serif", cursor: saving ? "not-allowed" : "pointer", fontSize: "0.9rem" }}>
              {saving ? "Saving..." : "Save Lead to Dashboard"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── ASSIGN LEAD MODAL (Superadmin only) ───────────────────────
function AssignLeadModal({ lead, agents, onAssign, onClose }) {
  const [selected, setSelected] = useState(lead.assigned_to || "");
  const [loading, setLoading]   = useState(false);

  async function handleAssign() {
    setLoading(true);
    try { await onAssign(lead.id, selected || null); onClose(); }
    finally { setLoading(false); }
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px" }}>
      <div style={{ background: "#FFFFFF", border: "1px solid rgba(80,200,120,0.3)", borderRadius: "14px", padding: "28px", width: "100%", maxWidth: "420px", position: "relative" }}>
        <button onClick={onClose} style={{ position: "absolute", top: "14px", right: "14px", background: "none", border: "none", color: "rgba(1,50,32,0.55)", cursor: "pointer" }}><X size={18} /></button>
        <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.4rem", color: "#013220", marginBottom: "4px" }}>Assign Lead</h3>
        <p style={{ color: "rgba(1,50,32,0.62)", fontSize: "0.82rem", marginBottom: "20px", fontFamily: "'Inter',sans-serif" }}>
          {lead.first_name} {lead.last_name} &nbsp;·&nbsp; {lead.phone}
        </p>
        <div style={{ marginBottom: "20px" }}>
          <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.78rem", marginBottom: "8px", fontFamily: "'Inter',sans-serif", letterSpacing: "0.08em", textTransform: "uppercase" }}>Assign To Agent</label>
          <select value={selected} onChange={e => setSelected(e.target.value)}
            style={{ width: "100%", padding: "11px 14px", background: "rgba(80,200,120,0.10)", border: "1.5px solid rgba(80,200,120,0.3)", borderRadius: "8px", color: "#013220", fontFamily: "'Inter',sans-serif", fontSize: "0.9rem", outline: "none", cursor: "pointer" }}>
            <option value="" style={{ background: "#FFFFFF" }}>— Unassigned —</option>
            {agents.map(a => (
              <option key={a.id} value={a.id} style={{ background: "#FFFFFF" }}>{a.name} ({a.email})</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={onClose} style={{ flex: 1, padding: "11px", background: "rgba(80,200,120,0.10)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "8px", color: "rgba(1,50,32,0.85)", cursor: "pointer", fontFamily: "'Inter',sans-serif" }}>Cancel</button>
          <button onClick={handleAssign} disabled={loading}
            style={{ flex: 2, padding: "11px", background: "linear-gradient(135deg,#50C878,#7FD9A0)", border: "none", borderRadius: "8px", color: "#FFFFFF", fontWeight: 700, cursor: loading ? "not-allowed" : "pointer", fontFamily: "'Inter',sans-serif", display: "flex", alignItems: "center", justifyContent: "center", gap: "7px" }}>
            <UserCheck size={15} /> {loading ? "Assigning..." : "Assign Lead"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── MANAGE AGENTS TAB (Superadmin only) ───────────────────────
function ManageAgentsPanel({ agents, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]   = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  async function handleCreate() {
    if (!form.email || !form.password || !form.name) { setError("All fields are required"); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    setSaving(true); setError("");
    try {
      await adminsAPI.create({ ...form, role: "agent" });
      setForm({ email: "", password: "", name: "" });
      setShowCreate(false);
      onRefresh();
    } catch (err) {
      setError(err.message || "Failed to create agent");
    } finally { setSaving(false); }
  }

  async function handleDeactivate(id) {
    if (!window.confirm("Deactivate this agent? They will lose dashboard access.")) return;
    try { await adminsAPI.delete(id); onRefresh(); }
    catch (err) { alert(err.message); }
  }

  const inp = {
    width: "100%", padding: "9px 12px",
    background: "rgba(80,200,120,0.10)", border: "1.5px solid rgba(80,200,120,0.25)",
    borderRadius: "6px", color: "#013220", fontFamily: "'Inter',sans-serif",
    fontSize: "0.88rem", outline: "none", boxSizing: "border-box",
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.4rem", color: "#013220", margin: "0 0 4px" }}>Sub-Admin Agents</h3>
          <p style={{ color: "rgba(1,50,32,0.48)", fontSize: "0.8rem", fontFamily: "'Inter',sans-serif", margin: 0 }}>
            Agents only see leads assigned to them by you.
          </p>
        </div>
        <button onClick={() => setShowCreate(v => !v)}
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "9px 18px", background: "rgba(80,200,120,0.12)", border: "1px solid rgba(80,200,120,0.4)", borderRadius: "6px", color: GOLD, cursor: "pointer", fontSize: "0.85rem", fontFamily: "'Inter',sans-serif", fontWeight: 600 }}>
          <Plus size={13} /> Add Agent
        </button>
      </div>

      {showCreate && (
        <div style={{ background: "rgba(80,200,120,0.04)", border: "1px solid rgba(80,200,120,0.2)", borderRadius: "10px", padding: "20px", marginBottom: "24px" }}>
          <h4 style={{ color: GOLD, fontFamily: "'Inter',sans-serif", fontSize: "0.8rem", letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: "14px" }}>New Agent Account</h4>
          {error && <p style={{ color: "#e05a5a", fontSize: "0.83rem", marginBottom: "12px" }}>{error}</p>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr auto", gap: "10px", alignItems: "end" }}>
            <div>
              <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.72rem", marginBottom: "4px" }}>FULL NAME</label>
              <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Agent name" style={inp} />
            </div>
            <div>
              <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.72rem", marginBottom: "4px" }}>EMAIL</label>
              <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} type="email" placeholder="agent@email.com" style={inp} />
            </div>
            <div>
              <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.72rem", marginBottom: "4px" }}>PASSWORD</label>
              <input value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} type="password" placeholder="Min 8 chars" style={inp} />
            </div>
            <button onClick={handleCreate} disabled={saving}
              style={{ padding: "9px 20px", background: "linear-gradient(135deg,#50C878,#7FD9A0)", border: "none", borderRadius: "6px", color: "#FFFFFF", fontWeight: 700, cursor: saving ? "not-allowed" : "pointer", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
              {saving ? "..." : "Create"}
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gap: "10px" }}>
        {agents.length === 0 && (
          <div style={{ padding: "32px", textAlign: "center", background: "rgba(80,200,120,0.05)", borderRadius: "10px", border: "1px dashed rgba(80,200,120,0.10)" }}>
            <p style={{ color: "rgba(1,50,32,0.48)", fontSize: "0.88rem", fontFamily: "'Inter',sans-serif" }}>No agents yet. Create one above to start assigning leads.</p>
          </div>
        )}
        {agents.map(agent => (
          <div key={agent.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", background: "rgba(80,200,120,0.05)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "8px" }}>
            <div>
              <p style={{ color: "#013220", fontFamily: "'Inter',sans-serif", fontWeight: 500, margin: "0 0 3px" }}>{agent.name}</p>
              <p style={{ color: "rgba(1,50,32,0.62)", fontSize: "0.8rem", fontFamily: "'Inter',sans-serif", margin: 0 }}>{agent.email}</p>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ padding: "3px 10px", background: agent.is_active ? "rgba(34,197,94,0.12)" : "rgba(239,68,68,0.12)", borderRadius: "4px", fontSize: "0.73rem", color: agent.is_active ? "#4ade80" : "#f87171", fontWeight: 600 }}>
                {agent.is_active ? "Active" : "Inactive"}
              </span>
              {agent.is_active && (
                <button onClick={() => handleDeactivate(agent.id)} title="Deactivate agent"
                  style={{ background: "none", border: "none", color: "rgba(239,68,68,0.6)", cursor: "pointer", display: "flex", padding: "4px" }}>
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── OMNIFLOW WHATSAPP TAB ─────────────────────────────────────
function OmniflowTab({ leads }) {
  const OMNIFLOW_URL = process.env.REACT_APP_OMNIFLOW_URL || "https://app.omniflow.com";
  const waLeads = leads.filter(l => l.channel === "WhatsApp");
  const WA_GREEN = "#25D366";
  const WA_ICON = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
  return (
    <div>
      <div style={{ padding: "20px 24px", background: "linear-gradient(135deg,rgba(37,211,102,0.08),rgba(37,211,102,0.03))", border: "1px solid rgba(37,211,102,0.25)", borderRadius: "10px", marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
          <div style={{ background: "rgba(37,211,102,0.15)", borderRadius: "10px", padding: "10px", display: "flex" }}>{WA_ICON}</div>
          <div>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.2rem", color: "#013220", margin: "0 0 4px" }}>Omniflow WhatsApp CRM</h3>
            <p style={{ color: "rgba(1,50,32,0.62)", fontSize: "0.8rem", fontFamily: "'Inter',sans-serif", margin: 0 }}>
              WhatsApp leads sync via webhook at <code style={{ color: WA_GREEN, background: "rgba(37,211,102,0.08)", padding: "2px 6px", borderRadius: "4px", fontSize: "0.75rem" }}>/api/webhooks/omniflow</code>
            </p>
          </div>
        </div>
        <a href={OMNIFLOW_URL} target="_blank" rel="noopener noreferrer"
          style={{ display: "flex", alignItems: "center", gap: "6px", padding: "9px 18px", background: WA_GREEN, borderRadius: "6px", color: "#013220", fontSize: "0.85rem", fontFamily: "'Inter',sans-serif", fontWeight: 600, textDecoration: "none" }}>
          {WA_ICON} Open Omniflow ↗
        </a>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "16px", marginBottom: "28px" }}>
        {[
          { label: "Total WA Leads", value: waLeads.length, color: WA_GREEN },
          { label: "New",        value: waLeads.filter(l => l.status === "New").length,       color: "#1B4A7A" },
          { label: "Contacted",  value: waLeads.filter(l => l.status === "Contacted").length, color: "#7FD9A0" },
          { label: "Qualified",  value: waLeads.filter(l => l.status === "Qualified").length, color: "#4ade80" },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ padding: "20px", background: "linear-gradient(135deg,#FFFFFF,#F7FBF8)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "10px" }}>
            <span style={{ fontSize: "0.72rem", color: "rgba(1,50,32,0.62)", letterSpacing: "0.08em", textTransform: "uppercase", display: "block", marginBottom: "8px" }}>{label}</span>
            <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2.2rem", fontWeight: 700, color, margin: 0, lineHeight: 1 }}>{value}</p>
          </div>
        ))}
      </div>
      <div style={{ overflowX: "auto", borderRadius: "10px", border: "1px solid rgba(80,200,120,0.10)" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "700px" }}>
          <thead>
            <tr style={{ background: "rgba(37,211,102,0.05)", borderBottom: "1px solid rgba(37,211,102,0.15)" }}>
              {["Date","Name","Phone","Property","Message","Status"].map(h => (
                <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.72rem", color: "rgba(1,50,32,0.62)", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {waLeads.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: "48px", textAlign: "center", color: "rgba(1,50,32,0.48)", fontSize: "0.88rem" }}>No WhatsApp leads yet.</td></tr>
            ) : waLeads.map((lead, i) => {
              const sc = STATUS_COLORS[lead.status] || {};
              return (
                <tr key={lead.id} style={{ borderBottom: "1px solid rgba(80,200,120,0.10)", background: i % 2 === 0 ? "transparent" : "rgba(80,200,120,0.05)" }}>
                  <td style={{ padding: "14px 16px", fontSize: "0.8rem", color: "rgba(1,50,32,0.62)", whiteSpace: "nowrap" }}>{new Date(lead.created_at).toLocaleDateString("en-IN")}</td>
                  <td style={{ padding: "14px 16px", fontSize: "0.88rem", color: "#013220", fontWeight: 500 }}>{lead.first_name} {lead.last_name}</td>
                  <td style={{ padding: "14px 16px" }}><a href={`https://wa.me/${lead.phone}`} target="_blank" rel="noopener noreferrer" style={{ color: WA_GREEN, textDecoration: "none" }}>{lead.phone}</a></td>
                  <td style={{ padding: "14px 16px", fontSize: "0.82rem", color: "rgba(1,50,32,0.62)" }}>{lead.property_title || "—"}</td>
                  <td style={{ padding: "14px 16px", fontSize: "0.8rem", color: "rgba(1,50,32,0.62)", maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.message || lead.notes || "—"}</td>
                  <td style={{ padding: "14px 16px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 10px", background: sc.bg, borderRadius: "4px", fontSize: "0.78rem", color: sc.color, fontWeight: 600 }}>{lead.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── MAIN ADMIN PAGE ───────────────────────────────────────────
export default function AdminPage() {
  const [admin, setAdmin]                   = useState(null);
  const [leads, setLeads]                   = useState([]);
  const [agents, setAgents]                 = useState([]);
  const [loading, setLoading]               = useState(false);
  const [search, setSearch]                 = useState("");
  const [searchName, setSearchName]         = useState("");
  const [searchPhone, setSearchPhone]       = useState("");
  const [searchEmail, setSearchEmail]       = useState("");
  const [searchProperty, setSearchProperty] = useState("");
  const [showAdvanced, setShowAdvanced]     = useState(false);
  const [channelFilter, setChannelFilter]   = useState("All");
  const [statusFilter, setStatusFilter]     = useState("All");
  const [showTelecaller, setShowTelecaller] = useState(false);
  const [activeTab, setActiveTab]           = useState("leads");
  const [assignModal, setAssignModal]       = useState(null);
  const [selectedIds, setSelectedIds]       = useState([]);
  const [bulkAgent, setBulkAgent]           = useState("");
  const [bulkLoading, setBulkLoading]       = useState(false);
  const [currentPage, setCurrentPage]       = useState(1);
  const PAGE_SIZE = 20;

  const isSuperAdmin = admin?.role === "superadmin";

  // Load admin on mount
  useEffect(() => {
    const token = localStorage.getItem("gbf_admin_token");
    if (token) {
      authAPI.me()
        .then(res => setAdmin(res.admin))
        .catch(() => localStorage.removeItem("gbf_admin_token"));
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsAPI.list({ limit: 500 });
      setLeads(res.data);
    } catch (err) {
      console.error("Failed to load leads:", err.message);
      setLeads([]);
    } finally { setLoading(false); }
  }, []);

  const refreshAgents = useCallback(async () => {
    if (!isSuperAdmin) return;
    try {
      const res = await adminsAPI.list();
      setAgents(res.data.filter(a => a.role !== "superadmin"));
    } catch (err) { console.error("Failed to load agents:", err.message); }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (admin) {
      refresh();
      if (admin.role === "superadmin") refreshAgents();
    }
  }, [admin, refresh, refreshAgents]);

  function handleLogout() {
    authAPI.logout();
    setAdmin(null);
    setLeads([]);
  }

  async function handleStatusChange(id, newStatus) {
    await leadsAPI.update(id, { status: newStatus });
    refresh();
  }

  async function handleAssignLead(leadId, agentId) {
    await leadsAPI.update(leadId, { assigned_to: agentId });
    refresh();
  }

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function toggleSelectAll(pageIds) {
    const allSelected = pageIds.length > 0 && pageIds.every(id => selectedIds.includes(id));
    if (allSelected) {
      setSelectedIds(prev => prev.filter(id => !pageIds.includes(id)));
    } else {
      setSelectedIds(prev => Array.from(new Set([...prev, ...pageIds])));
    }
  }

  async function handleBulkAssign() {
    if (selectedIds.length === 0) { alert("Select at least one lead first."); return; }
    setBulkLoading(true);
    try {
      await leadsAPI.bulkAssign(selectedIds, bulkAgent || null);
      setSelectedIds([]);
      setBulkAgent("");
      await refresh();
    } catch (err) {
      alert("Bulk assign failed: " + err.message);
    } finally {
      setBulkLoading(false);
    }
  }

  if (!admin) return <LoginScreen onLogin={setAdmin} />;

  // Filtering
  const filtered = leads.filter(l => {
    const q = search.toLowerCase();
    const basicMatch = !q
      || `${l.first_name} ${l.last_name}`.toLowerCase().includes(q)
      || l.phone?.includes(q)
      || l.email?.toLowerCase().includes(q)
      || l.property_title?.toLowerCase().includes(q);
    const advName     = !searchName     || `${l.first_name} ${l.last_name}`.toLowerCase().includes(searchName.toLowerCase());
    const advPhone    = !searchPhone    || l.phone?.includes(searchPhone);
    const advEmail    = !searchEmail    || l.email?.toLowerCase().includes(searchEmail.toLowerCase());
    const advProperty = !searchProperty || l.property_title?.toLowerCase().includes(searchProperty.toLowerCase());
    const matchChannel = channelFilter === "All" || l.channel === channelFilter;
    const matchStatus  = statusFilter  === "All" || l.status  === statusFilter;
    return basicMatch && advName && advPhone && advEmail && advProperty && matchChannel && matchStatus;
  });

  const totalPages   = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated    = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const todayStr     = new Date().toDateString();
  const todayLeads   = leads.filter(l => new Date(l.created_at).toDateString() === todayStr).length;
  const channelCounts = Object.values(LEAD_CHANNELS).reduce((acc, ch) => ({ ...acc, [ch]: leads.filter(l => l.channel === ch).length }), {});
  const propertyCounts = PROPERTIES.map(p => ({ ...p, leadCount: leads.filter(l => l.property_id === p.id).length })).sort((a, b) => b.leadCount - a.leadCount);

  const tabs = [
    ["leads", "All Leads"],
    ["by-property", "By Property"],
    ["analytics", "Analytics"],
    ["omniflow", "WhatsApp (Omniflow)"],
    ...(isSuperAdmin ? [["agents", "Manage Agents"]] : []),
  ];

  const PaginationBar = () => totalPages <= 1 ? null : (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px", flexWrap: "wrap", gap: "10px" }}>
      <span style={{ color: "rgba(1,50,32,0.62)", fontSize: "0.82rem", fontFamily: "'Inter',sans-serif" }}>
        Showing {((currentPage - 1) * PAGE_SIZE) + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length} leads
      </span>
      <div style={{ display: "flex", gap: "6px" }}>
        {[
          { label: "«",      action: () => setCurrentPage(1), disabled: currentPage === 1 },
          { label: "‹ Prev", action: () => setCurrentPage(p => Math.max(1, p - 1)), disabled: currentPage === 1 },
        ].map(({ label, action, disabled }) => (
          <button key={label} onClick={action} disabled={disabled}
            style={{ padding: "6px 12px", background: "rgba(80,200,120,0.10)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "6px", color: disabled ? "rgba(1,50,32,0.18)" : "#013220", cursor: disabled ? "not-allowed" : "pointer", fontSize: "0.82rem" }}>
            {label}
          </button>
        ))}
        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
          const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
          const page  = start + i;
          return page <= totalPages ? (
            <button key={page} onClick={() => setCurrentPage(page)}
              style={{ padding: "6px 12px", background: currentPage === page ? GOLD : "rgba(80,200,120,0.10)", border: `1px solid ${currentPage === page ? GOLD : "rgba(80,200,120,0.10)"}`, borderRadius: "6px", color: currentPage === page ? "#FFFFFF" : "#013220", cursor: "pointer", fontSize: "0.82rem", fontWeight: currentPage === page ? 700 : 400 }}>
              {page}
            </button>
          ) : null;
        })}
        {[
          { label: "Next ›", action: () => setCurrentPage(p => Math.min(totalPages, p + 1)), disabled: currentPage === totalPages },
          { label: "»",      action: () => setCurrentPage(totalPages), disabled: currentPage === totalPages },
        ].map(({ label, action, disabled }) => (
          <button key={label} onClick={action} disabled={disabled}
            style={{ padding: "6px 12px", background: "rgba(80,200,120,0.10)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "6px", color: disabled ? "rgba(1,50,32,0.18)" : "#013220", cursor: disabled ? "not-allowed" : "pointer", fontSize: "0.82rem" }}>
            {label}
          </button>
        ))}
      </div>
    </div>
  );

  return (
    <div style={{ background: "#F2F8F3", color: "#013220", minHeight: "100vh", paddingTop: "68px", fontFamily: "'Inter',sans-serif" }}>

      {/* ── Header ── */}
      <div style={{ background: "#F7FBF8", borderBottom: "1px solid rgba(80,200,120,0.15)", padding: "28px 24px" }}>
        <div style={{ maxWidth: "1400px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
          <div>
            <span style={{ fontSize: "0.7rem", color: GOLD, letterSpacing: "0.15em", textTransform: "uppercase" }}>
              {isSuperAdmin ? "Super Admin Dashboard" : "Agent Dashboard"}
            </span>
            <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.8rem", fontWeight: 600, color: "#013220", margin: "4px 0" }}>
              GroupBuy Flats — Lead Management
            </h1>
            <p style={{ color: "rgba(1,50,32,0.48)", fontSize: "0.8rem", margin: 0 }}>
              Logged in as <strong style={{ color: GOLD }}>{admin.email}</strong>
              {!isSuperAdmin && (
                <span style={{ marginLeft: "10px", padding: "2px 8px", background: "rgba(96,165,250,0.12)", borderRadius: "4px", fontSize: "0.73rem", color: "#1B4A7A" }}>
                  Agent · Viewing assigned leads only
                </span>
              )}
            </p>
          </div>
          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
            {isSuperAdmin && (
              <button onClick={() => setShowTelecaller(true)}
                style={{ display: "flex", alignItems: "center", gap: "7px", padding: "9px 18px", background: "rgba(80,200,120,0.12)", border: "1px solid rgba(80,200,120,0.4)", borderRadius: "6px", color: GOLD, cursor: "pointer", fontSize: "0.85rem", fontFamily: "'Inter',sans-serif", fontWeight: 600 }}>
                <Plus size={14} /> Direct Caller
              </button>
            )}
            <button onClick={() => leadsAPI.exportCSV()}
              style={{ display: "flex", alignItems: "center", gap: "7px", padding: "9px 18px", background: "rgba(80,200,120,0.10)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "6px", color: "rgba(1,50,32,0.85)", cursor: "pointer", fontSize: "0.85rem", fontFamily: "'Inter',sans-serif" }}>
              <Download size={14} /> Export CSV
            </button>
            <button onClick={refresh}
              style={{ padding: "9px 14px", background: "rgba(80,200,120,0.10)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "6px", color: "rgba(1,50,32,0.85)", cursor: "pointer" }}>
              <RefreshCw size={14} />
            </button>
            <button onClick={handleLogout}
              style={{ display: "flex", alignItems: "center", gap: "6px", padding: "9px 16px", background: "rgba(224,90,90,0.1)", border: "1px solid rgba(224,90,90,0.3)", borderRadius: "6px", color: "#e05a5a", cursor: "pointer", fontSize: "0.85rem", fontFamily: "'Inter',sans-serif" }}>
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: "1400px", margin: "0 auto", padding: "28px 24px" }}>

        {/* ── Stat Cards ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: "16px", marginBottom: "28px" }}>
          {[
            { label: "Total Leads",    value: leads.length,                               icon: Users,         color: GOLD },
            { label: "Today's Leads",  value: todayLeads,                                 icon: TrendingUp,    color: "#4ade80" },
            { label: "Web Leads",      value: channelCounts[LEAD_CHANNELS.WEB]      || 0, icon: Globe,         color: "#1B4A7A" },
            { label: "WhatsApp Leads", value: channelCounts[LEAD_CHANNELS.WHATSAPP] || 0, icon: MessageCircle, color: "#25D366" },
            { label: "Phone Leads",    value: channelCounts[LEAD_CHANNELS.PHONE]    || 0, icon: Phone,         color: "#f87171" },
            { label: "Chatbot Leads",  value: channelCounts[LEAD_CHANNELS.CHATBOT]  || 0, icon: MessageCircle, color: "#1B4A7A" },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} style={{ padding: "20px", background: "linear-gradient(135deg,#FFFFFF,#F7FBF8)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "10px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <span style={{ fontSize: "0.72rem", color: "rgba(1,50,32,0.62)", letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</span>
                <Icon size={15} color={color} />
              </div>
              <p style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "2.2rem", fontWeight: 700, color: "#013220", margin: 0, lineHeight: 1 }}>{value}</p>
            </div>
          ))}
        </div>

        {/* ── Tabs ── */}
        <div style={{ display: "flex", borderBottom: "1px solid rgba(80,200,120,0.15)", marginBottom: "24px", overflowX: "auto" }}>
          {tabs.map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              style={{ padding: "12px 24px", background: "none", border: "none", borderBottom: activeTab === key ? `2px solid ${GOLD}` : "2px solid transparent", color: activeTab === key ? GOLD : "rgba(1,50,32,0.62)", cursor: "pointer", fontFamily: "'Inter',sans-serif", fontSize: "0.9rem", fontWeight: activeTab === key ? 600 : 400, whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: "6px" }}>
              {key === "agents" && <Settings size={14} />}
              {label}
            </button>
          ))}
        </div>

        {/* ── ALL LEADS TAB ── */}
        {activeTab === "leads" && (
          <>
            <div style={{ marginBottom: "20px" }}>
              <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "10px" }}>
                <div style={{ position: "relative", flex: "1 1 240px" }}>
                  <Search size={14} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "rgba(1,50,32,0.48)" }} />
                  <input value={search} onChange={e => { setSearch(e.target.value); setCurrentPage(1); }}
                    placeholder="Quick search: name, phone, email, property..."
                    style={{ width: "100%", padding: "9px 12px 9px 36px", background: "rgba(80,200,120,0.10)", border: "1.5px solid rgba(80,200,120,0.2)", borderRadius: "6px", color: "#013220", fontFamily: "'Inter',sans-serif", fontSize: "0.85rem", outline: "none", boxSizing: "border-box" }}
                    onFocus={e => e.target.style.borderColor = GOLD} onBlur={e => e.target.style.borderColor = "rgba(80,200,120,0.2)"} />
                </div>
                <select value={channelFilter} onChange={e => { setChannelFilter(e.target.value); setCurrentPage(1); }}
                  style={{ padding: "9px 12px", background: "rgba(80,200,120,0.10)", border: "1.5px solid rgba(80,200,120,0.2)", borderRadius: "6px", color: channelFilter === "All" ? "rgba(1,50,32,0.62)" : "#013220", fontFamily: "'Inter',sans-serif", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}>
                  {["All", ...Object.values(LEAD_CHANNELS)].map(o => <option key={o} value={o} style={{ background: "#FFFFFF" }}>{o === "All" ? "All Channels" : o}</option>)}
                </select>
                <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  style={{ padding: "9px 12px", background: "rgba(80,200,120,0.10)", border: "1.5px solid rgba(80,200,120,0.2)", borderRadius: "6px", color: statusFilter === "All" ? "rgba(1,50,32,0.62)" : "#013220", fontFamily: "'Inter',sans-serif", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}>
                  {["All", ...STATUS_OPTIONS].map(o => <option key={o} value={o} style={{ background: "#FFFFFF" }}>{o === "All" ? "All Statuses" : o}</option>)}
                </select>
                <button onClick={() => setShowAdvanced(v => !v)}
                  style={{ padding: "9px 14px", background: showAdvanced ? "rgba(80,200,120,0.15)" : "rgba(80,200,120,0.10)", border: `1.5px solid ${showAdvanced ? GOLD : "rgba(80,200,120,0.10)"}`, borderRadius: "6px", color: showAdvanced ? GOLD : "rgba(1,50,32,0.85)", cursor: "pointer", fontSize: "0.82rem", fontFamily: "'Inter',sans-serif" }}>
                  ⚙ Advanced
                </button>
                <span style={{ display: "flex", alignItems: "center", color: "rgba(1,50,32,0.62)", fontSize: "0.82rem" }}>
                  {loading ? "Loading..." : `${filtered.length} lead${filtered.length !== 1 ? "s" : ""}`}
                </span>
              </div>

              {showAdvanced && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: "10px", padding: "16px", background: "rgba(80,200,120,0.04)", border: "1px solid rgba(80,200,120,0.15)", borderRadius: "8px" }}>
                  {[
                    { label: "Name",     value: searchName,     set: setSearchName,     ph: "Filter by name..." },
                    { label: "Phone",    value: searchPhone,    set: setSearchPhone,    ph: "Filter by phone..." },
                    { label: "Email",    value: searchEmail,    set: setSearchEmail,    ph: "Filter by email..." },
                    { label: "Property", value: searchProperty, set: setSearchProperty, ph: "Filter by property..." },
                  ].map(({ label, value, set, ph }) => (
                    <div key={label}>
                      <label style={{ display: "block", color: "rgba(1,50,32,0.62)", fontSize: "0.72rem", marginBottom: "4px", letterSpacing: "0.08em" }}>{label.toUpperCase()}</label>
                      <input value={value} onChange={e => { set(e.target.value); setCurrentPage(1); }} placeholder={ph}
                        style={{ width: "100%", padding: "8px 12px", background: "rgba(80,200,120,0.10)", border: "1.5px solid rgba(80,200,120,0.2)", borderRadius: "6px", color: "#013220", fontFamily: "'Inter',sans-serif", fontSize: "0.83rem", outline: "none", boxSizing: "border-box" }} />
                    </div>
                  ))}
                  <div style={{ display: "flex", alignItems: "flex-end" }}>
                    <button onClick={() => { setSearchName(""); setSearchPhone(""); setSearchEmail(""); setSearchProperty(""); setSearch(""); setCurrentPage(1); }}
                      style={{ padding: "8px 16px", background: "rgba(224,90,90,0.1)", border: "1px solid rgba(224,90,90,0.3)", borderRadius: "6px", color: "#e05a5a", cursor: "pointer", fontSize: "0.82rem" }}>
                      Clear All
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Bulk Assign Bar (Superadmin only) */}
            {isSuperAdmin && selectedIds.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", padding: "14px 18px", marginBottom: "14px", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: "10px" }}>
                <span style={{ color: "#1B4A7A", fontFamily: "'Inter',sans-serif", fontSize: "0.9rem", fontWeight: 600 }}>
                  {selectedIds.length} lead{selectedIds.length > 1 ? "s" : ""} selected
                </span>
                <span style={{ color: "rgba(1,50,32,0.62)", fontSize: "0.85rem" }}>Assign to:</span>
                <select
                  value={bulkAgent}
                  onChange={e => setBulkAgent(e.target.value)}
                  style={{ padding: "8px 12px", background: "rgba(80,200,120,0.10)", border: "1.5px solid rgba(167,139,250,0.4)", borderRadius: "6px", color: "#013220", fontFamily: "'Inter',sans-serif", fontSize: "0.85rem", outline: "none", cursor: "pointer", minWidth: "180px" }}
                >
                  <option value="" style={{ background: "#FFFFFF" }}>— Unassign —</option>
                  {agents.map(a => (
                    <option key={a.id} value={a.id} style={{ background: "#FFFFFF" }}>{a.name}</option>
                  ))}
                </select>
                <button
                  onClick={handleBulkAssign}
                  disabled={bulkLoading}
                  style={{ display: "flex", alignItems: "center", gap: "6px", padding: "8px 18px", background: "#1B4A7A", border: "none", borderRadius: "6px", color: "#FFFFFF", fontWeight: 700, fontFamily: "'Inter',sans-serif", fontSize: "0.85rem", cursor: bulkLoading ? "not-allowed" : "pointer", opacity: bulkLoading ? 0.6 : 1 }}
                >
                  <UserCheck size={15} /> {bulkLoading ? "Assigning..." : "Apply to Selected"}
                </button>
                <button
                  onClick={() => setSelectedIds([])}
                  style={{ padding: "8px 14px", background: "transparent", border: "1px solid rgba(1,50,32,0.18)", borderRadius: "6px", color: "rgba(1,50,32,0.85)", cursor: "pointer", fontSize: "0.82rem" }}
                >
                  Clear
                </button>
              </div>
            )}

            {/* Leads Table */}
            <div style={{ overflowX: "auto", borderRadius: "10px", border: "1px solid rgba(80,200,120,0.10)" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isSuperAdmin ? "1000px" : "800px" }}>
                <thead>
                  <tr style={{ background: "rgba(80,200,120,0.06)", borderBottom: "1px solid rgba(80,200,120,0.2)" }}>
                    {isSuperAdmin && (
                      <th style={{ padding: "12px 16px", textAlign: "center", width: "44px" }}>
                        <input
                          type="checkbox"
                          aria-label="Select all on page"
                          checked={paginated.length > 0 && paginated.every(l => selectedIds.includes(l.id))}
                          onChange={() => toggleSelectAll(paginated.map(l => l.id))}
                          style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#1B4A7A" }}
                        />
                      </th>
                    )}
                    {[
                      "Date","Name","Phone","Email","Channel","Property","Assigned To","Status","Notes",
                      ...(isSuperAdmin ? ["Assign"] : [])
                    ].map(h => (
                      <th key={h} style={{ padding: "12px 16px", textAlign: "left", fontSize: "0.72rem", color: "rgba(1,50,32,0.62)", letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={isSuperAdmin ? 11 : 9} style={{ padding: "48px", textAlign: "center", color: "rgba(1,50,32,0.48)" }}>Loading leads...</td></tr>
                  ) : filtered.length === 0 ? (
                    <tr><td colSpan={isSuperAdmin ? 11 : 9} style={{ padding: "48px", textAlign: "center", color: "rgba(1,50,32,0.48)", fontSize: "0.9rem" }}>
                      {leads.length === 0
                        ? (isSuperAdmin ? "No leads yet. Submit a form on the website to see leads here." : "No leads assigned to you yet. Ask the super admin to assign leads.")
                        : "No leads match your filters."}
                    </td></tr>
                  ) : paginated.map((lead, i) => {
                    const ChIcon = CHANNEL_ICONS[lead.channel] || Globe;
                    const sc = STATUS_COLORS[lead.status] || {};
                    return (
                      <tr key={lead.id} style={{ borderBottom: "1px solid rgba(80,200,120,0.10)", background: selectedIds.includes(lead.id) ? "rgba(167,139,250,0.10)" : (i % 2 === 0 ? "transparent" : "rgba(80,200,120,0.05)") }}>
                        {isSuperAdmin && (
                          <td style={{ padding: "14px 16px", textAlign: "center" }}>
                            <input
                              type="checkbox"
                              aria-label={`Select ${lead.first_name}`}
                              checked={selectedIds.includes(lead.id)}
                              onChange={() => toggleSelect(lead.id)}
                              style={{ width: "16px", height: "16px", cursor: "pointer", accentColor: "#1B4A7A" }}
                            />
                          </td>
                        )}
                        <td style={{ padding: "14px 16px", fontSize: "0.8rem", color: "rgba(1,50,32,0.62)", whiteSpace: "nowrap" }}>
                          {new Date(lead.created_at).toLocaleDateString("en-IN")}<br/>
                          <span style={{ fontSize: "0.72rem" }}>{new Date(lead.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "0.88rem", color: "#013220", fontWeight: 500 }}>{lead.first_name} {lead.last_name}</td>
                        <td style={{ padding: "14px 16px", fontSize: "0.85rem" }}>
                          <a href={`tel:${lead.phone}`} style={{ color: GOLD, textDecoration: "none" }}>{lead.phone}</a>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "0.82rem", color: "rgba(1,50,32,0.62)" }}>{lead.email || "—"}</td>
                        <td style={{ padding: "14px 16px" }}>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", background: "rgba(80,200,120,0.10)", borderRadius: "4px", padding: "4px 9px", fontSize: "0.78rem", color: "rgba(1,50,32,0.85)" }}>
                            <ChIcon size={12} /> {lead.channel}
                          </span>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "0.82rem", color: "rgba(1,50,32,0.62)" }}>{lead.property_title || "—"}</td>
                        <td style={{ padding: "14px 16px" }}>
                          {lead.assigned_name
                            ? <span style={{ padding: "3px 9px", background: "rgba(167,139,250,0.12)", borderRadius: "4px", color: "#1B4A7A", fontSize: "0.78rem", fontWeight: 500 }}>{lead.assigned_name}</span>
                            : <span style={{ color: "rgba(1,50,32,0.18)", fontSize: "0.78rem" }}>Unassigned</span>
                          }
                        </td>
                        <td style={{ padding: "14px 16px" }}>
                          <select value={lead.status} onChange={e => handleStatusChange(lead.id, e.target.value)}
                            style={{ padding: "5px 8px", background: sc.bg || "rgba(80,200,120,0.10)", border: "none", borderRadius: "4px", color: sc.color || "#013220", fontFamily: "'Inter',sans-serif", fontSize: "0.78rem", cursor: "pointer", outline: "none" }}>
                            {STATUS_OPTIONS.map(s => <option key={s} value={s} style={{ background: "#FFFFFF", color: "#013220" }}>{s}</option>)}
                          </select>
                        </td>
                        <td style={{ padding: "14px 16px", fontSize: "0.8rem", color: "rgba(1,50,32,0.62)", maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lead.message || lead.notes || "—"}
                        </td>
                        {isSuperAdmin && (
                          <td style={{ padding: "14px 16px" }}>
                            <button onClick={() => setAssignModal(lead)}
                              style={{ display: "flex", alignItems: "center", gap: "5px", padding: "6px 12px", background: "rgba(167,139,250,0.1)", border: "1px solid rgba(167,139,250,0.3)", borderRadius: "5px", color: "#1B4A7A", cursor: "pointer", fontSize: "0.75rem", fontFamily: "'Inter',sans-serif", whiteSpace: "nowrap" }}>
                              <UserCheck size={12} /> Assign
                            </button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <PaginationBar />
          </>
        )}

        {/* ── BY PROPERTY TAB ── */}
        {activeTab === "by-property" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: "16px" }}>
            {propertyCounts.map(p => (
              <div key={p.id} style={{ padding: "20px", background: "linear-gradient(135deg,#FFFFFF,#F7FBF8)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "10px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "12px" }}>
                  <div>
                    <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.1rem", color: "#013220", margin: "0 0 2px" }}>{p.title}</h3>
                    <p style={{ fontSize: "0.75rem", color: "rgba(1,50,32,0.62)", margin: 0 }}>{p.area} · {p.type}</p>
                  </div>
                  <span style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.8rem", fontWeight: 700, color: p.leadCount > 0 ? GOLD : "rgba(1,50,32,0.18)", lineHeight: 1 }}>{p.leadCount}</span>
                </div>
                <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                  {Object.values(LEAD_CHANNELS).map(ch => {
                    const count = leads.filter(l => l.property_id === p.id && l.channel === ch).length;
                    return count > 0 ? <span key={ch} style={{ background: "rgba(80,200,120,0.10)", borderRadius: "4px", padding: "3px 8px", fontSize: "0.72rem", color: "rgba(1,50,32,0.85)" }}>{ch}: {count}</span> : null;
                  })}
                  {p.leadCount === 0 && <span style={{ color: "rgba(1,50,32,0.48)", fontSize: "0.8rem" }}>No leads yet</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── ANALYTICS TAB ── */}
        {activeTab === "analytics" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: "24px" }}>
            <div style={{ padding: "24px", background: "linear-gradient(135deg,#FFFFFF,#F7FBF8)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "10px" }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.2rem", color: "#013220", marginBottom: "20px" }}>Leads by Channel</h3>
              {Object.values(LEAD_CHANNELS).map(ch => {
                const count = channelCounts[ch] || 0;
                const pct = leads.length ? Math.round((count / leads.length) * 100) : 0;
                return (
                  <div key={ch} style={{ marginBottom: "14px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
                      <span style={{ fontSize: "0.85rem", color: "rgba(1,50,32,0.85)" }}>{ch}</span>
                      <span style={{ fontSize: "0.85rem", color: GOLD, fontWeight: 600 }}>{count}</span>
                    </div>
                    <div style={{ height: "6px", background: "rgba(80,200,120,0.10)", borderRadius: "3px", overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${pct}%`, background: "linear-gradient(90deg,#50C878,#7FD9A0)", borderRadius: "3px", transition: "width 0.4s" }} />
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: "24px", background: "linear-gradient(135deg,#FFFFFF,#F7FBF8)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "10px" }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.2rem", color: "#013220", marginBottom: "20px" }}>Leads by Status</h3>
              {STATUS_OPTIONS.map(status => {
                const count = leads.filter(l => l.status === status).length;
                const sc = STATUS_COLORS[status];
                return (
                  <div key={status} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: sc.bg, borderRadius: "6px", marginBottom: "8px" }}>
                    <span style={{ fontSize: "0.88rem", color: sc.color, fontWeight: 500 }}>{status}</span>
                    <span style={{ fontSize: "1.2rem", fontWeight: 700, color: sc.color }}>{count}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: "24px", background: "linear-gradient(135deg,#FFFFFF,#F7FBF8)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "10px" }}>
              <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "1.2rem", color: "#013220", marginBottom: "20px" }}>Top Properties by Leads</h3>
              {propertyCounts.slice(0, 5).map((p, i) => (
                <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: "1px solid rgba(80,200,120,0.10)" }}>
                  <span style={{ fontSize: "0.85rem", color: "rgba(1,50,32,0.85)" }}>
                    <span style={{ color: GOLD, marginRight: "8px", fontWeight: 600 }}>#{i + 1}</span>{p.title}
                  </span>
                  <span style={{ fontSize: "0.9rem", color: GOLD, fontWeight: 600 }}>{p.leadCount}</span>
                </div>
              ))}
              {propertyCounts.filter(p => p.leadCount > 0).length === 0 && (
                <p style={{ color: "rgba(1,50,32,0.48)", fontSize: "0.85rem" }}>No leads yet.</p>
              )}
            </div>
          </div>
        )}

        {/* ── OMNIFLOW TAB ── */}
        {activeTab === "omniflow" && <OmniflowTab leads={leads} />}

        {/* ── MANAGE AGENTS TAB (Superadmin only) ── */}
        {activeTab === "agents" && isSuperAdmin && (
          <div style={{ padding: "28px", background: "linear-gradient(135deg,#FFFFFF,#F7FBF8)", border: "1px solid rgba(80,200,120,0.10)", borderRadius: "10px" }}>
            <ManageAgentsPanel agents={agents} onRefresh={refreshAgents} />
          </div>
        )}

      </div>

      {showTelecaller && <TelecallerForm onSave={refresh} onClose={() => setShowTelecaller(false)} />}
      {assignModal && (
        <AssignLeadModal
          lead={assignModal}
          agents={agents}
          onAssign={handleAssignLead}
          onClose={() => setAssignModal(null)}
        />
      )}
    </div>
  );
}
